import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import moment from 'moment';
import { client } from '../../../../../App';
import { GET_AUTOMATED_BAR_CHART } from '../../../../../queries/widgets';
import {
  buildAlignedAutomatedBarChartSeries,
  buildRollingPeriodLabels,
} from '../../../../../utils/formatBarChartPeriodLabel';
import {
  formatRollingPeriodAxisLabel,
  normalizeRollingPerformance,
  SQUAD_ROLLING_BAR_COLORS,
  type RollingPerformanceConfig,
} from './rollingPerformance';
import {
  SQUAD_ROLLING_CHART_GRID_PADDING,
  SQUAD_ROLLING_CHART_HEIGHT_PX,
  SQUAD_ROLLING_CHART_Y_LABEL_OFFSET_X,
} from './squadPortfolioChartUtils';
import { SQUAD_CHART_AXIS_LABEL_PX } from './pageHeaderTokens';
import {
  captureSquadChart,
  SQUAD_CHART_FONT_STACK,
  useSquadChartFontStatus,
} from './squadChartFont';
import {
  layoutSquadRollingDataLabels,
  SQUAD_ROLLING_LABEL_INSIDE_COLORS,
} from './squadRollingDataLabels';
import { SQUAD_COLORS, SQUAD_DEFAULT_ISIN } from './templateConstants';

export const SQUAD_ROLLING_CHART_ID = 'squad-rolling-bar';

function formatDateToUntil(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined;
  const raw = String(dateStr).trim();
  const isoPart = raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (isoPart && moment(isoPart, 'YYYY-MM-DD', true).isValid()) return isoPart;
  const m = moment(raw);
  return m.isValid() ? m.format('YYYY-MM-DD') : undefined;
}

type Props = {
  data?: Partial<RollingPerformanceConfig> | null;
  historyIsin?: string;
  publishedAt?: string;
  height?: number;
  /** FS-Header primary (brutto) / secondary (netto). */
  primaryColor?: string;
  secondaryColor?: string;
  onHasDataChange?: (hasData: boolean) => void;
};

const formatPctLabel = (val: number) =>
  `${Number(val).toFixed(2).replace('.', ',')}%`;

export const SquadRollingPerformanceChart: FC<Props> = ({
  data,
  historyIsin,
  publishedAt,
  height = SQUAD_ROLLING_CHART_HEIGHT_PX,
  primaryColor,
  secondaryColor,
  onHasDataChange,
}) => {
  const fallbackIsin = historyIsin || SQUAD_DEFAULT_ISIN;
  const chartFontStatus = useSquadChartFontStatus();
  const rolling = useMemo(
    () => normalizeRollingPerformance(data, fallbackIsin),
    [data, fallbackIsin],
  );

  const rollingChartType = rolling.chartType;
  const rollingIsin = rolling.isin || fallbackIsin;
  const rollingAutomationType = rolling.automationType;
  const rollingBarColorSets = useMemo(() => {
    const primary = String(primaryColor || '').trim();
    const secondary = String(secondaryColor || '').trim();
    if (primary && secondary) return [primary, secondary];
    if (primary) return [primary, SQUAD_ROLLING_BAR_COLORS[1]];
    if (secondary) return [SQUAD_ROLLING_BAR_COLORS[0], secondary];
    return SQUAD_ROLLING_BAR_COLORS;
  }, [primaryColor, secondaryColor]);

  const [automatedPeriodsData, setAutomatedPeriodsData] = useState<
    Array<{ name: string; data: number[]; color?: string; missing?: boolean[] }>
  >([{ name: '', data: [] }]);
  const [automatedPeriodLabels, setAutomatedPeriodLabels] = useState<string[]>([]);
  const [barChartLoading, setBarChartLoading] = useState(false);

  const untilForApi = useMemo(
    () => formatDateToUntil(publishedAt),
    [publishedAt],
  );

  const fetchKey = useMemo(
    () => [rollingChartType, rollingIsin, rollingAutomationType, untilForApi || ''].join('|'),
    [rollingChartType, rollingIsin, rollingAutomationType, untilForApi],
  );

  useEffect(() => {
    if (!rollingIsin || rollingChartType !== 'Automated') return;

    let cancelled = false;

    const load = async () => {
      setBarChartLoading(true);
      try {
        const result = await client.query({
          query: GET_AUTOMATED_BAR_CHART,
          variables: {
            isin: rollingIsin,
            type: rollingAutomationType,
            ...(untilForApi ? { until: untilForApi } : {}),
            ...(untilForApi ? { useFactsheetUntilForCurrentYear: true } : {}),
          },
          fetchPolicy: 'network-only',
        });

        if (cancelled) return;

        const apiData = result.data?.getAutomatedBarChart;
        if (!Array.isArray(apiData) || apiData.length === 0) {
          setAutomatedPeriodLabels([]);
          setAutomatedPeriodsData([{ name: '', data: [] }]);
          return;
        }

        if (rollingAutomationType === 'all') {
          const { labels, series } = buildAlignedAutomatedBarChartSeries(apiData, {
            legend: rolling.legend,
            colors: rollingBarColorSets,
            fallbackColor: SQUAD_COLORS.navy,
          });
          setAutomatedPeriodLabels(labels);
          setAutomatedPeriodsData(
            series.map((item) => ({
              name: item.name,
              data: item.data.map((value) => (value == null ? 0 : value)),
              color: item.color,
              missing: item.data.map((value) => value == null),
            })),
          );
        } else {
          const labelRows = [...apiData].sort(
            (a: any, b: any) =>
              moment(a.end, 'DD.MM.YYYY', true).valueOf() -
              moment(b.end, 'DD.MM.YYYY', true).valueOf(),
          );
          setAutomatedPeriodLabels(buildRollingPeriodLabels(labelRows));
          setAutomatedPeriodsData([
            {
              name: '',
              data: labelRows.map((item: any) => item.value),
              missing: labelRows.map((item: any) => item.value == null),
            },
          ]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch SQUAD rolling bar chart:', error);
          setAutomatedPeriodLabels([]);
          setAutomatedPeriodsData([{ name: '', data: [] }]);
        }
      } finally {
        if (!cancelled) setBarChartLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    fetchKey,
    rolling.legend,
    rollingAutomationType,
    rollingBarColorSets,
    rollingChartType,
    rollingIsin,
    untilForApi,
  ]);

  const isBarAutomated = rollingChartType === 'Automated';

  const barChartCategories = useMemo(() => {
    if (isBarAutomated) return automatedPeriodLabels;
    return (rolling.periods || []).map((p) => p.label);
  }, [isBarAutomated, automatedPeriodLabels, rolling.periods]);

  const barChartSeries = useMemo(() => {
    if (isBarAutomated) {
      if (automatedPeriodsData.length > 0 && automatedPeriodsData[0]?.data?.length > 0) {
        return automatedPeriodsData.map((series, index) => ({
          name: series.name || '',
          data: series.data,
          color: rollingBarColorSets[index] || series.color,
          missing: series.missing || series.data.map((value) => value == null),
        }));
      }
      return [];
    }

    const periods = rolling.periods || [];
    if (!periods.length) return [];

    const hasDual = periods.some((p) => p.brutto != null || p.netto != null);
    if (hasDual) {
      return [
        {
          name: rolling.legend?.gross_perf_year_as_of_today || 'brutto',
          data: periods.map((p) => Number(p.brutto ?? p.value ?? 0)),
          color: rollingBarColorSets[0],
          missing: periods.map((p) => p.brutto == null && p.value == null),
        },
        {
          name: rolling.legend?.perf_year_as_of_today || 'netto (mit 5% Ausgabeaufschlag)',
          data: periods.map((p) => Number(p.netto ?? p.value ?? 0)),
          color: rollingBarColorSets[1] || rollingBarColorSets[0],
          missing: periods.map((p) => p.netto == null && p.value == null),
        },
      ];
    }

    return [
      {
        name: '',
        data: periods.map((p) => Number(p.value ?? p.brutto ?? 0)),
        color: rollingBarColorSets[0],
        missing: periods.map((p) => p.value == null && p.brutto == null),
      },
    ];
  }, [isBarAutomated, automatedPeriodsData, rolling.periods, rolling.legend, rollingBarColorSets]);

  /**
   * A missing period is drawn as 0 so the bars stay aligned, but must not print "0,00%" —
   * only a real 0 does.
   */
  const apexSeries = useMemo(
    () => barChartSeries.map(({ name, data, color }) => ({ name, data, color })),
    [barChartSeries],
  );
  const missingPoints = useMemo(
    () => barChartSeries.map((series) => series.missing),
    [barChartSeries],
  );

  const hasBarChartData =
    barChartSeries.length > 0 && barChartSeries.some((s) => s.data?.length > 0);

  useEffect(() => {
    onHasDataChange?.(hasBarChartData);
  }, [hasBarChartData, onHasDataChange]);

  const showLegend =
    isBarAutomated ? rollingAutomationType === 'all' : barChartSeries.length > 1;

  const barChartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        id: SQUAD_ROLLING_CHART_ID,
        type: 'bar',
        height,
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: SQUAD_CHART_FONT_STACK,
        // No chart title here, so the 15 px ApexCharts reserves above one is dead space.
        parentHeightOffset: 0,
        // Labels that do not fit their bar move beyond its end — see squadRollingDataLabels.
        events: { mounted: layoutSquadRollingDataLabels, updated: layoutSquadRollingDataLabels },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '85%',
          dataLabels: { position: 'center' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number, opts?: { seriesIndex?: number; dataPointIndex?: number }) =>
          val == null || missingPoints[opts?.seriesIndex ?? -1]?.[opts?.dataPointIndex ?? -1]
            ? ''
            : formatPctLabel(val),
        style: {
          fontSize: '9px',
          fontWeight: 600,
          colors: SQUAD_ROLLING_LABEL_INSIDE_COLORS,
        },
        dropShadow: { enabled: false },
      },
      stroke: { show: true, width: 0, colors: ['transparent'] },
      xaxis: {
        categories: barChartCategories,
        labels: {
          rotate: 0,
          rotateAlways: false,
          trim: false,
          hideOverlappingLabels: false,
          minHeight: 28,
          maxHeight: 40,
          offsetY: 0,
          // One line per period (not formatBarChartPeriodLabel's 3-line split) while they fit.
          formatter: (val: string) =>
            formatRollingPeriodAxisLabel(val, barChartCategories.length),
          // A string, not an array: ApexCharts reads an array per label, so the old
          // one-entry array coloured the first period navy and left the rest grey.
          style: { fontSize: `${SQUAD_CHART_AXIS_LABEL_PX}px`, colors: SQUAD_COLORS.text },
        },
      },
      yaxis: {
        labels: {
          formatter: (value: number) => `${value}%`,
          offsetX: SQUAD_ROLLING_CHART_Y_LABEL_OFFSET_X,
          style: { fontSize: `${SQUAD_CHART_AXIS_LABEL_PX}px`, colors: SQUAD_COLORS.text },
        },
      },
      grid: {
        borderColor: '#E5E5E5',
        strokeDashArray: 0,
        padding: { ...SQUAD_ROLLING_CHART_GRID_PADDING },
      },
      legend: {
        show: showLegend,
        position: 'bottom',
        fontSize: '10px',
        labels: { colors: SQUAD_COLORS.text },
        markers: { size: 5, shape: 'square' },
      },
      colors: rollingBarColorSets,
      fill: { opacity: 1 },
      tooltip: {
        y: { formatter: (val: number) => formatPctLabel(val) },
      },
    }),
    [barChartCategories, height, missingPoints, rollingBarColorSets, showLegend],
  );

  if (hasBarChartData) {
    return (
      <Box sx={{ width: '100%', minHeight: height }}>
        {/* Mounted once the chart font is there: ApexCharts measures its labels on the first draw. */}
        {chartFontStatus !== 'loading' ? (
          <ReactApexChart
            key={`${rollingBarColorSets.join('-')}-${chartFontStatus}`}
            type="bar"
            height={height}
            options={barChartOptions}
            series={apexSeries}
          />
        ) : null}
      </Box>
    );
  }

  if (isBarAutomated && barChartLoading) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontSize: 12,
        }}
      >
        Chart wird geladen…
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        fontSize: 12,
        border: `1px dashed ${SQUAD_COLORS.gold}`,
        borderRadius: 1,
      }}
    >
      Keine Rollierende-Wertentwicklung-Daten
    </Box>
  );
};

export function captureSquadRollingChart(): Promise<string | undefined> {
  return captureSquadChart(SQUAD_ROLLING_CHART_ID);
}
